import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { toast as message } from 'sonner'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Field, MoneyInput, SelectField } from '../../components/ui/controls'
import { DataTable, type Column } from '../../components/ui/data-table'
import {
  checkPayoutEligibility,
  createPayout,
  getBankAccounts,
  getPayoutEwallet,
  getPersonalPayoutAccounts,
} from '../../services/payout'
import type {
  CreatePayoutMethod,
  PayoutEligibilityResponse,
  PayoutEwallet,
} from '../../services/payout'
import type {
  BankAccount,
  PersonalPayoutAccount,
  PayoutCapability,
} from '../../types'
import {
  calculatePayoutFee,
  calculatePayoutAmountFromNet,
  compareDecimalAmounts,
  formatDecimalAmount,
  formatDecimalPercent,
  isZeroDecimal,
  isPositiveMultipleOf,
} from '../../utils/decimalAmount'

type PayoutRateRow = {
  key: string
  channel: string
  fee: string
  minimum: string
}

type UsdtServiceType = 'ORDINARY' | 'URGENT'

interface PayoutFormValues {
  amount: string
  netAmount?: string
  method: CreatePayoutMethod
  bankAccountId?: string
  personalPayoutAccountId?: string
  totpCode?: string
}

const PAYOUT_CAPABILITY_ROWS: Array<{
  capability: PayoutCapability
  channel: string
}> = [
  { capability: 'OVERSEAS_BUSINESS_ACCOUNT', channel: '海外企業賬戶' },
  {
    capability: 'OVERSEAS_EWALLET_ORDINARY',
    channel: '海外電子錢包（普通提現）',
  },
  {
    capability: 'OVERSEAS_EWALLET_URGENT',
    channel: '海外電子錢包（緊急提現）',
  },
  { capability: 'PERSONAL_EWALLET', channel: '個人電子錢包' },
  { capability: 'PERSONAL_BANK', channel: '個人銀行賬戶' },
]

const formatProcessingDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString('zh-CN', {
    timeZone: 'Asia/Shanghai',
  })

function isCertificationApproved(
  method: CreatePayoutMethod,
  eligibility: PayoutEligibilityResponse,
): boolean {
  if (method === 'BANK_BUSINESS') return eligibility.businessKybApproved
  if (method === 'PERSONAL_BANK' || method === 'PERSONAL_EWALLET')
    return eligibility.personalKycApproved
  return eligibility.personalKycApproved || eligibility.businessKybApproved
}

function certificationRequirement(method: CreatePayoutMethod): string {
  if (method === 'BANK_BUSINESS') return '需先通過企業 KYB'
  if (method === 'PERSONAL_BANK' || method === 'PERSONAL_EWALLET')
    return '需先通過個人 KYC'
  return '需先通過個人 KYC 或企業 KYB'
}

export default function PayoutCreate() {
  const [eligibility, setEligibility] =
    useState<PayoutEligibilityResponse | null>(null)
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [personalAccounts, setPersonalAccounts] = useState<
    PersonalPayoutAccount[]
  >([])
  const [payoutEwallet, setPayoutEwallet] = useState<PayoutEwallet | null>(null)
  const [payoutEwalletLoading, setPayoutEwalletLoading] = useState(true)
  const [payoutEwalletLoadError, setPayoutEwalletLoadError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [criticalLoadError, setCriticalLoadError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [amount, setAmount] = useState<string | null>(null)
  const [method, setMethod] = useState<CreatePayoutMethod | undefined>()
  const [selectedUsdtServiceType, setSelectedUsdtServiceType] =
    useState<UsdtServiceType>()
  const form = useForm<PayoutFormValues>({ shouldUnregister: true })
  const navigate = useNavigate()
  const businessKybApproved = eligibility?.businessKybApproved ?? false
  const personalKycApproved = eligibility?.personalKycApproved ?? false
  const capabilities = new Set(eligibility?.enabledCapabilities ?? [])
  const methodOptions = [
    ...(capabilities.has('OVERSEAS_BUSINESS_ACCOUNT')
      ? [
          {
            value: 'BANK_BUSINESS' as const,
            label: '海外企業賬戶',
            disabled: !businessKybApproved,
            title: businessKybApproved
              ? undefined
              : certificationRequirement('BANK_BUSINESS'),
          },
        ]
      : []),
    ...(capabilities.has('OVERSEAS_EWALLET_ORDINARY') ||
    capabilities.has('OVERSEAS_EWALLET_URGENT')
      ? [
          {
            value: 'USDT' as const,
            label: '海外電子錢包',
            disabled: !personalKycApproved && !businessKybApproved,
            title:
              personalKycApproved || businessKybApproved
                ? undefined
                : certificationRequirement('USDT'),
          },
        ]
      : []),
    ...(capabilities.has('PERSONAL_EWALLET')
      ? [
          {
            value: 'PERSONAL_EWALLET' as const,
            label: '個人電子錢包',
            disabled: !personalKycApproved,
            title: personalKycApproved
              ? undefined
              : certificationRequirement('PERSONAL_EWALLET'),
          },
        ]
      : []),
    ...(capabilities.has('PERSONAL_BANK')
      ? [
          {
            value: 'PERSONAL_BANK' as const,
            label: '個人銀行賬戶',
            disabled: !personalKycApproved,
            title: personalKycApproved
              ? undefined
              : certificationRequirement('PERSONAL_BANK'),
          },
        ]
      : []),
  ]
  const usdtServiceOptions = [
    ...(capabilities.has('OVERSEAS_EWALLET_ORDINARY')
      ? [{ label: '普通提現', value: 'ORDINARY' as const }]
      : []),
    ...(capabilities.has('OVERSEAS_EWALLET_URGENT')
      ? [{ label: '緊急提現', value: 'URGENT' as const }]
      : []),
  ]
  const usdtServiceType =
    selectedUsdtServiceType === 'ORDINARY' &&
    capabilities.has('OVERSEAS_EWALLET_ORDINARY')
      ? selectedUsdtServiceType
      : selectedUsdtServiceType === 'URGENT' &&
          capabilities.has('OVERSEAS_EWALLET_URGENT')
        ? selectedUsdtServiceType
        : capabilities.has('OVERSEAS_EWALLET_ORDINARY')
          ? 'ORDINARY'
          : capabilities.has('OVERSEAS_EWALLET_URGENT')
            ? 'URGENT'
            : undefined

  const loadPayoutEwallet = useCallback(async () => {
    setPayoutEwalletLoading(true)
    setPayoutEwalletLoadError(false)
    try {
      const response = await getPayoutEwallet()
      setPayoutEwallet(response.data ?? null)
    } catch {
      setPayoutEwalletLoadError(true)
    } finally {
      setPayoutEwalletLoading(false)
    }
  }, [])

  const loadCriticalData = useCallback(async () => {
    setLoading(true)
    setCriticalLoadError(false)
    setEligibility(null)
    try {
      const [eligibilityRes, accountsRes, personalAccountsRes] =
        await Promise.all([
          checkPayoutEligibility(),
          getBankAccounts(),
          getPersonalPayoutAccounts(),
        ])
      setEligibility(eligibilityRes.data)
      setBankAccounts(accountsRes.data)
      setPersonalAccounts(personalAccountsRes.data)
    } catch {
      setCriticalLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadCriticalData()
    void loadPayoutEwallet()
  }, [loadCriticalData, loadPayoutEwallet])

  const handleSubmit = async (values: PayoutFormValues) => {
    if (!eligibility || !isCertificationApproved(values.method, eligibility)) {
      message.error(certificationRequirement(values.method))
      return
    }
    if (values.method === 'USDT' && !payoutEwallet) {
      message.error('請先添加海外電子錢包地址')
      return
    }
    setSubmitting(true)
    try {
      await createPayout({
        amount: values.amount,
        method: values.method,
        bankAccountId:
          values.method === 'BANK_BUSINESS' ? values.bankAccountId : undefined,
        personalPayoutAccountId:
          values.method === 'PERSONAL_BANK' ||
          values.method === 'PERSONAL_EWALLET'
            ? values.personalPayoutAccountId
            : undefined,
        usdtServiceType: values.method === 'USDT' ? usdtServiceType : undefined,
        totpCode: values.totpCode,
      })
      message.success('結款申請已提交')
      navigate('/dashboard/payouts')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '提交失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const formatAmount = (amt: string | number) =>
    formatDecimalAmount(String(amt))
  const formatMoney = (amt: string | number) =>
    `$${formatDecimalAmount(String(amt))}`
  const formatFee = (rate: string, fixed?: string | null) => {
    const parts: string[] = []
    if (!isZeroDecimal(rate)) parts.push(formatDecimalPercent(rate))
    if (fixed != null && !isZeroDecimal(fixed)) parts.push(formatMoney(fixed))
    return parts.length > 0 ? parts.join(' + ') : formatMoney(0)
  }
  const payoutRateRows: PayoutRateRow[] = eligibility
    ? PAYOUT_CAPABILITY_ROWS.filter(({ capability }) =>
        capabilities.has(capability),
      ).map(({ capability, channel }) => ({
        key: capability,
        channel,
        fee: formatFee(
          eligibility.feeConfigs[capability].feeRate,
          eligibility.feeConfigs[capability].fixedFee,
        ),
        minimum: eligibility.minPayoutAmounts[capability],
      }))
    : []

  const payoutRateColumns: Column<PayoutRateRow>[] = [
    { key: 'channel', header: '結款渠道', render: (r) => r.channel },
    {
      key: 'fee',
      header: '費率',
      className: 'text-right tabular-nums',
      render: (r) => <span className="text-primary">{r.fee}</span>,
    },
    {
      key: 'minimum',
      header: '最低結款金額',
      className: 'text-right tabular-nums',
      render: (r) => `${formatMoney(r.minimum)} USD`,
    },
  ]

  const selectedCapability: PayoutCapability | undefined =
    method === 'BANK_BUSINESS'
      ? 'OVERSEAS_BUSINESS_ACCOUNT'
      : method === 'USDT'
        ? usdtServiceType === 'URGENT'
          ? 'OVERSEAS_EWALLET_URGENT'
          : usdtServiceType === 'ORDINARY'
            ? 'OVERSEAS_EWALLET_ORDINARY'
            : undefined
        : method === 'PERSONAL_EWALLET'
          ? 'PERSONAL_EWALLET'
          : method === 'PERSONAL_BANK'
            ? 'PERSONAL_BANK'
            : undefined
  const selectedFeeConfig =
    eligibility && selectedCapability
      ? eligibility.feeConfigs[selectedCapability]
      : undefined
  const selectedMinimum =
    eligibility && selectedCapability
      ? eligibility.minPayoutAmounts[selectedCapability]
      : undefined

  useEffect(() => {
    if (form.getValues('amount') != null) {
      void form.trigger('amount')
    }
  }, [form, selectedMinimum])

  const feeEstimate =
    amount && /^\d+(\.\d+)?$/.test(amount) && selectedFeeConfig
      ? calculatePayoutFee(
          amount,
          selectedFeeConfig.feeRate,
          selectedFeeConfig.fixedFee,
        )
      : null

  const selectedFeeRate = selectedFeeConfig?.feeRate
  const selectedFixedFee = selectedFeeConfig?.fixedFee

  useEffect(() => {
    if (!method || selectedFeeRate == null || selectedFixedFee == null) return
    const currentAmount = form.getValues('amount') as string | null | undefined
    form.setValue(
      'netAmount',
      currentAmount && /^\d+(\.\d+)?$/.test(currentAmount)
        ? calculatePayoutFee(currentAmount, selectedFeeRate, selectedFixedFee)
            .netAmount
        : '',
    )
  }, [form, method, selectedFeeRate, selectedFixedFee])

  const handleAmountChange = (value: string | null) => {
    setAmount(value)
    if (value && !/^\d+(\.\d+)?$/.test(value)) return
    if (method && selectedFeeConfig) {
      form.setValue(
        'netAmount',
        value == null
          ? ''
          : calculatePayoutFee(
              value,
              selectedFeeConfig.feeRate,
              selectedFeeConfig.fixedFee,
            ).netAmount,
      )
      if (value != null) void form.trigger('netAmount')
    }
  }

  const handleNetAmountChange = (value: string | null) => {
    const grossAmount =
      value == null || !selectedFeeConfig
        ? null
        : calculatePayoutAmountFromNet(
            value,
            selectedFeeConfig.feeRate,
            selectedFeeConfig.fixedFee,
          )
    setAmount(grossAmount)
    form.setValue('amount', grossAmount ?? '')
    if (grossAmount != null) void form.trigger('amount')
  }

  const selectedUsdtRate = selectedFeeConfig?.feeRate ?? '0'
  const selectedUsdtFixed = selectedFeeConfig?.fixedFee ?? '0'
  const selectedExpectedProcessingAt = eligibility
    ? usdtServiceType === 'URGENT'
      ? eligibility.usdtUrgentExpectedProcessingAt
      : eligibility.usdtOrdinaryExpectedProcessingAt
    : undefined

  if (loading)
    return (
      <Card className="max-w-2xl p-6">
        <p role="status">載入中…</p>
      </Card>
    )
  if (criticalLoadError || !eligibility)
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">發起結款</h1>
        <div role="alert" className="rounded-xl bg-red-50 p-4">
          <h2>結款資料載入失敗</h2>
          <p>無法載入結款資格或收款賬戶資料，請重新載入。</p>
          <Button onClick={() => void loadCriticalData()}>重新載入</Button>
        </div>
      </div>
    )
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">發起結款</h1>
      {!eligibility.eligible && (
        <div role="alert" className="rounded-xl bg-amber-50 p-4">
          <h2>結款暫時不可用</h2>
          <p>{eligibility.reasons.join('；')}</p>
        </div>
      )}
      <Card className="max-w-3xl p-5">
        <h2 className="font-semibold">賬戶資訊</h2>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">可用餘額</dt>
            <dd>${formatAmount(eligibility.availableBalance)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">最低結款金額</dt>
            <dd>
              {selectedMinimum ? `${formatMoney(selectedMinimum)} USD` : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">KYC 狀態</dt>
            <dd>{personalKycApproved ? '已通過' : '未通過'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">KYB 狀態</dt>
            <dd>{businessKybApproved ? '已通過' : '未通過'}</dd>
          </div>
        </dl>
        <h2 className="font-semibold">結款費率</h2>
        <DataTable
          columns={payoutRateColumns}
          data={payoutRateRows}
          rowKey="key"
          emptyText="暫無結款費率資料"
        />
      </Card>
      <Card className="max-w-2xl p-5">
        <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
          <Field
            label="結款方式"
            htmlFor="payout-method"
            required
            error={form.formState.errors.method?.message}
          >
            <Controller
              name="method"
              control={form.control}
              rules={{ required: '請選擇結款方式' }}
              render={({ field }) => (
                <SelectField
                  id="payout-method"
                  value={field.value}
                  options={methodOptions}
                  onValueChange={(value) => {
                    field.onChange(value)
                    setMethod(value as CreatePayoutMethod)
                    form.setValue('bankAccountId', undefined)
                    form.setValue('personalPayoutAccountId', undefined)
                  }}
                />
              )}
            />
          </Field>
          {method === 'PERSONAL_BANK' && (
            <div className="rounded-lg bg-primary/5 p-4 text-sm">
              <h3 className="font-medium">個人銀行提款每週五集中處理</h3>
              <p>
                北京時間週五 16:00 前提交可於當日處理，16:00
                起提交順延至下週五。扣除手續費後的到賬金額須為 500.00 USD
                的正整數倍。
              </p>
              {eligibility.personalBankExpectedProcessingAt && (
                <p>
                  預計處理日期：
                  {formatProcessingDate(
                    eligibility.personalBankExpectedProcessingAt,
                  )}
                </p>
              )}
            </div>
          )}
          {method === 'USDT' ? (
            <>
              <Field label="提現類型" htmlFor="usdt-service" required>
                <SelectField
                  id="usdt-service"
                  value={usdtServiceType}
                  onValueChange={(v) =>
                    setSelectedUsdtServiceType(v as UsdtServiceType)
                  }
                  options={usdtServiceOptions}
                />
              </Field>
              <div className="space-y-2 rounded-lg bg-primary/5 p-4 text-sm">
                <h3>
                  {usdtServiceType === 'URGENT' ? '緊急提現' : '普通提現'}
                </h3>
                <p>{formatFee(selectedUsdtRate, selectedUsdtFixed)}</p>
                <p>
                  {usdtServiceType === 'URGENT'
                    ? '香港工作日優先處理，預計當日到賬；非香港工作日提交後 24 小時（D+1）內到賬。'
                    : '香港工作日提現，16:00 前提交預計下一香港工作日（T+1）到賬；16:00 起順延一個受理工作日。'}
                </p>
                {selectedExpectedProcessingAt && (
                  <p>
                    {usdtServiceType === 'URGENT'
                      ? '預計到賬時間'
                      : '預計到賬日期'}
                    ：
                    {usdtServiceType === 'URGENT'
                      ? new Date(selectedExpectedProcessingAt).toLocaleString(
                          'zh-CN',
                          { timeZone: 'Asia/Hong_Kong', hour12: false },
                        )
                      : formatProcessingDate(selectedExpectedProcessingAt)}
                  </p>
                )}
                <p>實際審核及到賬時間由管理員安排。</p>
              </div>
              {payoutEwalletLoading ? (
                <p role="status">載入中…</p>
              ) : payoutEwalletLoadError ? (
                <div role="alert">
                  海外電子錢包地址載入失敗{' '}
                  <Button onClick={loadPayoutEwallet}>重新載入</Button>
                </div>
              ) : payoutEwallet ? (
                <dl>
                  <dt>鏈</dt>
                  <dd>{payoutEwallet.chain}</dd>
                  <dt>錢包地址</dt>
                  <dd className="break-all">{payoutEwallet.address}</dd>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      void navigator.clipboard.writeText(payoutEwallet.address)
                    }
                  >
                    複製地址
                  </Button>
                </dl>
              ) : (
                <div role="alert">
                  尚未添加海外電子錢包地址{' '}
                  <Button onClick={() => navigate('/dashboard/bank-accounts')}>
                    前往添加
                  </Button>
                </div>
              )}
            </>
          ) : method === 'PERSONAL_BANK' || method === 'PERSONAL_EWALLET' ? (
            <Field
              label="個人提款賬戶"
              htmlFor="personal-account"
              required
              error={form.formState.errors.personalPayoutAccountId?.message}
            >
              <Controller
                name="personalPayoutAccountId"
                control={form.control}
                rules={{
                  required:
                    method === 'PERSONAL_BANK' || method === 'PERSONAL_EWALLET'
                      ? '請選擇個人提款賬戶'
                      : false,
                }}
                render={({ field }) => (
                  <SelectField
                    id="personal-account"
                    value={field.value}
                    placeholder="選擇個人提款賬戶"
                    onValueChange={field.onChange}
                    options={personalAccounts
                      .filter((acc) =>
                        method === 'PERSONAL_BANK'
                          ? acc.accountKind === 'BANK'
                          : acc.accountKind === 'E_WALLET',
                      )
                      .map((acc) => ({
                        value: acc.id,
                        label:
                          acc.accountKind === 'BANK'
                            ? `${acc.bankName || acc.paymentMethod} - ****${acc.accountNumberLast4 || ''}`
                            : `${acc.paymentMethod} - ${acc.fields?.[0]?.displayValue || acc.fields?.[0]?.value || acc.currency}`,
                      }))}
                  />
                )}
              />
            </Field>
          ) : (
            <Field
              label="銀行賬戶"
              htmlFor="bank-account"
              required={method === 'BANK_BUSINESS'}
              error={form.formState.errors.bankAccountId?.message}
            >
              <Controller
                name="bankAccountId"
                control={form.control}
                rules={{
                  required:
                    method === 'BANK_BUSINESS' ? '請選擇銀行賬戶' : false,
                }}
                render={({ field }) => (
                  <SelectField
                    id="bank-account"
                    value={field.value}
                    placeholder="選擇銀行賬戶"
                    onValueChange={field.onChange}
                    options={bankAccounts.map((acc) => ({
                      value: acc.id,
                      label: `${acc.bankName} - ****${acc.accountNumberLast4}`,
                    }))}
                  />
                )}
              />
            </Field>
          )}
          <Field
            label="結款金額"
            htmlFor="payout-amount"
            required
            error={form.formState.errors.amount?.message}
          >
            <Controller
              name="amount"
              control={form.control}
              rules={{
                required: '請輸入結款金額',
                validate: (value) => {
                  if (!/^\d+(\.\d{1,2})?$/.test(value))
                    return '請輸入有效的結款金額（最多兩位小數）'
                  if (
                    selectedMinimum &&
                    compareDecimalAmounts(value, selectedMinimum) < 0
                  )
                    return `最低結款金額為 ${formatMoney(selectedMinimum)} USD`
                  if (
                    compareDecimalAmounts(value, eligibility.availableBalance) >
                    0
                  )
                    return '結款金額超過可用餘額'
                  return true
                },
              }}
              render={({ field }) => (
                <MoneyInput
                  id="payout-amount"
                  value={field.value ?? ''}
                  onValueChange={(value) => {
                    field.onChange(value)
                    handleAmountChange(value || null)
                  }}
                  step="0.01"
                />
              )}
            />
          </Field>
          {method && selectedFeeConfig && (
            <Field
              label="到賬金額"
              htmlFor="payout-net"
              required
              error={form.formState.errors.netAmount?.message}
            >
              <Controller
                name="netAmount"
                control={form.control}
                rules={{
                  required: '請輸入到賬金額',
                  validate: (value) => {
                    if (!value || !/^\d+(\.\d{1,2})?$/.test(value))
                      return '請輸入有效的到賬金額（最多兩位小數）'
                    if (
                      method === 'PERSONAL_BANK' &&
                      !isPositiveMultipleOf(value, '500')
                    )
                      return '到賬金額須為 500.00 USD 的正整數倍'
                    return (
                      calculatePayoutAmountFromNet(
                        value,
                        selectedFeeConfig.feeRate,
                        selectedFeeConfig.fixedFee,
                      ) != null || '目前費率無法計算此到賬金額的結款金額'
                    )
                  },
                }}
                render={({ field }) => (
                  <MoneyInput
                    id="payout-net"
                    value={field.value ?? ''}
                    onValueChange={(value) => {
                      field.onChange(value)
                      handleNetAmountChange(value || null)
                    }}
                    step={method === 'PERSONAL_BANK' ? '500' : '0.01'}
                  />
                )}
              />
            </Field>
          )}
          {feeEstimate && amount && (
            <div className="rounded-lg bg-muted p-4" aria-live="polite">
              預估手續費{' '}
              <strong className="ml-3">
                {formatMoney(feeEstimate.totalFee)} USD
              </strong>
            </div>
          )}
          {eligibility.twoFactorEnabled && (
            <Field
              label="2FA 驗證碼"
              htmlFor="payout-totp"
              required
              error={form.formState.errors.totpCode?.message}
            >
              <Input
                id="payout-totp"
                maxLength={6}
                {...form.register('totpCode', {
                  required: '請輸入 2FA 驗證碼',
                })}
              />
            </Field>
          )}
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={
                submitting ||
                !eligibility.eligible ||
                !method ||
                !isCertificationApproved(method, eligibility) ||
                (method === 'USDT' &&
                  (payoutEwalletLoading ||
                    payoutEwalletLoadError ||
                    !payoutEwallet))
              }
            >
              提交結款申請
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/payouts')}
            >
              取消
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
